import { useCallback, useEffect, useRef, useState } from 'react';

import { BaseAlert } from '../../components/UI/Alert/types';

export type AlertOptions = BaseAlert & {
  message: string;
};

type AlertState = BaseAlert & {
  isOpen: boolean;
  message: string;
};

export const useAlert = () => {
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [alertState, setAlertState] = useState<AlertState>({
    isOpen: false,
    message: '',
  });

  const showAlert = useCallback((options: AlertOptions) => {
    if (!isMountedRef.current) return;
    // Remove focus from trigger element to prevent aria-hidden accessibility warning
    (document.activeElement as HTMLElement)?.blur();
    setAlertState({ isOpen: true, ...options });
  }, []);

  const hideAlert = useCallback(() => {
    if (!isMountedRef.current) return;
    setAlertState((prev) => {
      // Execute callback after setState completes to avoid updating parent during render
      if (prev.onDidDismiss) {
        setTimeout(prev.onDidDismiss, 0);
      }
      return { ...prev, isOpen: false };
    });
  }, []);

  return {
    alertState,
    showAlert,
    hideAlert,
  };
};
